import i18n from '../i18n/config';

/**
 * Structured error body the backend returns for failures the user can act on.
 *
 * `message` is a readable fallback (English, same as the rest of the API);
 * `code` is what lets the frontend show a translated message instead.
 */
type ApiErrorDetail = {
  code?: unknown;
  message?: unknown;
  provider?: unknown;
};

const asDetail = (err: unknown): string | ApiErrorDetail | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const maybeAxios = err as { response?: { data?: { detail?: unknown; message?: unknown } } };
  const detail = maybeAxios.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail as ApiErrorDetail;
  const msg = maybeAxios.response?.data?.message;
  return typeof msg === 'string' ? msg : undefined;
};

export const extractApiErrorMessage = (err: unknown, fallback: string): string => {
  const detail = asDetail(err);
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

/** Error codes the backend attaches to an unusable AI provider (HTTP 424). */
const LLM_PROVIDER_ERROR_KEYS: Record<string, string> = {
  llm_provider_missing_key: 'errors.llmProvider.missingKey',
  llm_provider_invalid_key: 'errors.llmProvider.invalidKey',
};

/**
 * Message for an API error, translated when the backend named a known cause.
 *
 * Prefer this over `extractApiErrorMessage` on any surface that triggers an AI
 * call: without it an org-admin whose provider key is missing or wrong only
 * sees axios' "Request failed with status code 424", which names neither the
 * provider nor the fix. Anything the backend does not code falls back to its
 * message, so unknown errors are never swallowed.
 */
export const translateApiError = (err: unknown, fallback: string): string => {
  const detail = asDetail(err);
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
    const key = LLM_PROVIDER_ERROR_KEYS[detail.code];
    if (key) {
      const provider =
        typeof detail.provider === 'string' && detail.provider.trim()
          ? detail.provider
          : i18n.t('errors.llmProvider.unknownProvider', 'IA');
      return i18n.t(key, { provider });
    }
  }
  return extractApiErrorMessage(err, fallback);
};

/** The organization a 404 names when a super admin opens another org's object. */
export type OtherOrganizationRef = { id: string; name: string };

/**
 * The org that owns the object behind a 404, when the backend tells (super
 * admins only, `detail.code === "other_organization"`); null for any other error.
 */
export const getOtherOrganization = (err: unknown): OtherOrganizationRef | null => {
  if (!err || typeof err !== 'object') return null;
  const response = (err as { response?: { status?: number } }).response;
  if (response?.status !== 404) return null;
  const detail = asDetail(err);
  if (!detail || typeof detail !== 'object' || detail.code !== 'other_organization') return null;
  const org = (detail as { organization?: unknown }).organization;
  if (!org || typeof org !== 'object') return null;
  const { id, name } = org as { id?: unknown; name?: unknown };
  return typeof id === 'string' && typeof name === 'string' ? { id, name } : null;
};
